export const dateRangeOptions = [
  { value: 'today', label: 'Today' },
  { value: 'last_2_days', label: 'Last 2 days' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
] as const;

// Company event types
const billingEventKeys = [
  'billing_address_updated',
  'payment_source_added',
  'payment_source_updated',
  'payment_source_deleted',
  'payment_source_expiring',
  'payment_source_expired',
  'coupon_applied',
  'exclusive_offer_requested',
  'subscription_cancellation_requested',
  'subscription_cancellation_scheduled',
  'subscription_downgraded',
] as const;
const companyEventKeys = ['company_updated', 'company_ownership_transferred'] as const;
const membersEventKeys = ['user_updated', 'user_email_changed', 'user_invited', 'user_removed'] as const;
const integrationEventKeys = [
  'integration_created',
  'integration_disconnected',
  'integration_reconnected',
  'integration_removed',
  'integration_member_created',
  'integration_member_removed',
  'custom_integration_created',
  'custom_integration_updated',
  'sharefile_integration_created',
] as const;

// Escrow event types
const escrowGeneralEventKeys = [
  'escrow_created',
  'escrow_signed',
  'escrow_activated',
  'escrow_removed',
  'escrow_pending_activation',
] as const;
const escrowAssetEventKeys = [
  'asset_created',
  'asset_removed',
  'asset_connected',
  'asset_disconnected',
  'legacy_repo_migrated',
] as const;
const escrowAgreementEventKeys = [
  'agreement_generated',
  'agreement_sent_for_review_depositor',
  'agreement_sent_for_review_beneficiary',
  'agreement_resent_for_review_depositor',
  'agreement_resent_for_review_beneficiary',
  'agreement_approved',
] as const;
const changeRequestsEventKeys = [
  'suggested_change_created',
  'suggested_change_accepted',
  'suggested_change_rejected',
  'suggested_change_admin_rejected',
  'suggested_change_applied',
] as const;
const escrowInvitationEventKeys = [
  'escrow_invitation_sent',
  'escrow_invitation_accepted',
  'escrow_invitation_rejected',
] as const;
const escrowMembersEventKeys = [
  'escrow_membership_created',
  'escrow_membership_updated',
  'escrow_membership_removed',
] as const;
const continuityProviderEventKeys = [
  'continuity_provider_created',
  'continuity_provider_removed',
  'continuity_provider_confirmed',
  'continuity_provider_access_issues',
] as const;
const escrowReportEventKeys = [
  'escrow_reports_disabled',
  'escrow_verified_addon_enabled',
  'escrow_certified_addon_requested',
  'continuity_escrow_report_generated',
] as const;
const escrowSignatureEventKeys = ['signature_signed', 'signatures_requested'] as const;
const escrowTerminationEventKeys = [
  'escrow_termination_requested',
  'escrow_termination_accepted',
  'escrow_termination_rejected',
  'escrow_termination_admin_accepted',
  'escrow_termination_admin_declined',
  'escrow_terminated',
] as const;

// Custodian event types
const custodianGeneralEventKeys = [
  'custodian_profile_created',
  'custodian_profile_sent_for_review',
  'custodian_profile_removed',
  'custodian_profile_activated',
];
const custodianAssetEventKeys = ['custodian_asset_created', 'custodian_asset_updated', 'custodian_asset_removed'];
const custodianOperationEventKeys = [
  'custodian_operation_created',
  'custodian_operation_updated',
  'custodian_operation_removed',
];
const custodianTerminationEventKeys = [
  'custodian_profile_terminated',
  'custodian_termination_requested',
  'custodian_termination_rejected',
] as const;

// Backup event types
const backupGeneralEventKeys = [
  'backup_created',
  'backup_setup',
  'backup_removed',
  'backup_terminated',
  'backup_activated',
  'backup_pending_activation',
  'backup_updated',
] as const;
const backupAssetEventKeys = [
  'backup_asset_created',
  'backup_asset_connected',
  'backup_asset_disconnected',
  'backup_asset_removed',
  'backup_version_downloaded',
  'backup_credential_created',
  'backup_credential_removed',
  'backup_credential_downloaded',
];
const backupMemberEventKeys = ['backup_member_created', 'backup_member_updated', 'backup_member_removed'];
const backupReportMemberEventKeys = ['backup_reports_enabled', 'backup_reports_disabled'];

export const filterKeyToEventTypes = {
  'organization.all': [...billingEventKeys, ...companyEventKeys, ...membersEventKeys, ...integrationEventKeys],
  'organization.billing': billingEventKeys,
  'organization.company': companyEventKeys,
  'organization.members': membersEventKeys,
  'organization.integrations': integrationEventKeys,
  // Escrow
  'escrow.all': [
    ...escrowGeneralEventKeys,
    ...escrowAssetEventKeys,
    ...escrowAgreementEventKeys,
    ...changeRequestsEventKeys,
    ...escrowInvitationEventKeys,
    ...escrowMembersEventKeys,
    ...continuityProviderEventKeys,
    ...escrowReportEventKeys,
    ...escrowSignatureEventKeys,
    ...escrowTerminationEventKeys,
  ],
  'escrow.general': escrowGeneralEventKeys,
  'escrow.assets': escrowAssetEventKeys,
  'escrow.agreements': escrowAgreementEventKeys,
  'escrow.change-requests': changeRequestsEventKeys,
  'escrow.invitation': escrowInvitationEventKeys,
  'escrow.members': escrowMembersEventKeys,
  'escrow.continuity': continuityProviderEventKeys,
  'escrow.reports': escrowReportEventKeys,
  'escrow.signatures': escrowSignatureEventKeys,
  'escrow.terminations': escrowTerminationEventKeys,
  // Custodian
  'custodian.all': [
    ...custodianGeneralEventKeys,
    ...custodianAssetEventKeys,
    ...custodianOperationEventKeys,
    ...custodianTerminationEventKeys,
  ],
  'custodian.general': custodianGeneralEventKeys,
  'custodian.assets': custodianAssetEventKeys,
  'custodian.operations': custodianOperationEventKeys,
  'custodian.terminations': custodianTerminationEventKeys,
  // Backup
  'backups.all': [
    ...backupGeneralEventKeys,
    ...backupAssetEventKeys,
    ...backupMemberEventKeys,
    ...backupReportMemberEventKeys,
  ],
  'backups.general': backupGeneralEventKeys,
  'backups.assets': backupAssetEventKeys,
  'backups.members': backupMemberEventKeys,
  'backups.reports': backupReportMemberEventKeys,
} as const;

export type IFilterKey = keyof typeof filterKeyToEventTypes;

export const filterKeyGroups: { label: string; options: { value: IFilterKey; label: string }[] }[] = [
  {
    label: 'Organization',
    options: [
      { value: 'organization.all', label: 'All Organization' },
      { value: 'organization.billing', label: 'Billing' },
      { value: 'organization.company', label: 'Company' },
      { value: 'organization.integrations', label: 'Integrations' },
      { value: 'organization.members', label: 'Team Members' },
    ],
  },
  {
    label: 'Escrows',
    options: [
      { value: 'escrow.all', label: 'All Escrows' },
      { value: 'escrow.general', label: 'Escrows General' },
      { value: 'escrow.assets', label: 'Escrows Assets (Software & SaaS)' },
      { value: 'escrow.agreements', label: 'Escrows Agreements' },
      { value: 'escrow.change-requests', label: 'Escrows Change requests' },
      { value: 'escrow.invitation', label: 'Escrows Invitations' },
      { value: 'escrow.members', label: 'Escrows Members' },
      { value: 'escrow.continuity', label: 'Escrows Providers (Continuity)' },
      { value: 'escrow.reports', label: 'Escrows Reports' },
      { value: 'escrow.signatures', label: 'Escrows Signatures' },
      { value: 'escrow.terminations', label: 'Escrows Terminations' },
    ],
  },
  {
    label: 'Software Backups',
    options: [
      { value: 'backups.all', label: 'All Backups' },
      { value: 'backups.general', label: 'Backups General' },
      { value: 'backups.assets', label: 'Backups Assets' },
      { value: 'backups.members', label: 'Backups Members' },
      { value: 'backups.reports', label: 'Backups Reports' },
    ],
  },
  {
    label: 'Software Custodian',
    options: [
      { value: 'custodian.all', label: 'All Custodian' },
      { value: 'custodian.general', label: 'Custodian General' },
      { value: 'custodian.assets', label: 'Custodian Assets' },
      { value: 'custodian.operations', label: 'Custodian Operations' },
      { value: 'custodian.terminations', label: 'Custodian Terminations' },
    ],
  },
];
